/**
 * A counter that advances on an interval, for screens that claim to be live.
 *
 * It exists because of a bug found by looking at the app rather than by running the tests. The
 * sidebar strip said "Waiting for an assistant · nothing has connected yet" and the tokens list
 * said "Created, but no assistant has connected with it" — while an assistant had, in fact, just
 * connected with that very token. Neither screen was wrong when it loaded; both were showing an
 * answer fetched thirty seconds earlier and never asked again.
 *
 * That is worse than showing nothing. "Waiting" is a claim about the present tense, and a screen
 * that makes it from stale data is telling somebody their install failed when it worked. So
 * anything that renders a live-sounding sentence polls, and stops polling when the answer can no
 * longer change.
 *
 * Two things it does not do: it does not poll a hidden tab, because a background tab asking a
 * question nobody is reading is somebody else's battery; and it advances immediately when a hidden
 * tab is brought back, because the first thing a returning person looks at must not be a minute old.
 */

import { useEffect, useState } from "react";

export function usePollTick(intervalMs: number, enabled = true): number {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const timer = setInterval(() => {
      if (document.visibilityState === "visible") setTick((n) => n + 1);
    }, intervalMs);

    function onVisible() {
      if (document.visibilityState === "visible") setTick((n) => n + 1);
    }
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [intervalMs, enabled]);

  return tick;
}

/** How often a live-sounding card asks again. Cheap enough to leave open, quick enough to feel live. */
export const LIVE_POLL_MS = 5000;

/**
 * Slower, for lists where the live fact is secondary — the tokens list is mostly names and dates,
 * and only `last_used_at` moves on its own.
 */
export const SLOW_POLL_MS = 15000;
